import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import Sidebar from '../components/Sidebar';
import axios from 'axios';
import common from '../utils/common';
import { authStorage } from '../utils/authStorage';
import { fetchParticipants } from '../features/api/apiSlice';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import './ntc-branding.css';

const Upcoming = () => {
  const dispatch = useAppDispatch();
  const { participants: reduxParticipants } = useAppSelector((state) => state.api);
  
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [interests, setInterests] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [addInterest, setAddInterest] = useState(false);
  const [showAddInterestBtn, setShowAddInterestBtn] = useState(true);
  const [loginData, setLoginData] = useState(null);
  const [remainingTime, setRemainingTime] = useState(0);
  const [showAddProjectModal, setShowAddProjectModal] = useState(false);
  const [isSavingProject, setIsSavingProject] = useState(false);
  const [isSubmittingInterest, setIsSubmittingInterest] = useState(false);
  const [projectModalMode, setProjectModalMode] = useState('add'); // 'add', 'edit', 'view'
  const [editingProject, setEditingProject] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [originalProjects, setOriginalProjects] = useState([]);
  
  const [interestForm, setInterestForm] = useState({
    proj_id: 0,
    part_id: 0,
    amount: '',
    wallet_add: '',
    reason_for_interest: ''
  });

  const [projectForm, setProjectForm] = useState({
    projectName: '',
    projectURL: '',
    whitepaperURL: '',
    allocation: '',
    allocation_currency: 'USDT',
    tokenomics: '',
    dealanalysis: '',
    fund_wallet: '',
    timer: '24',
    telegram: '',
    poc_email: '',
    whatsapp: '',
    referred: false,
    referred_by: 0
  });

  // Custom Alert State
  const [customAlert, setCustomAlert] = useState({
    show: false,
    message: '',
    type: 'error'
  });

  const showAlert = (message, type = 'error') => {
    setCustomAlert({ show: true, message, type });
    setTimeout(() => {
      setCustomAlert({ show: false, message: '', type: 'error' });
    }, type === 'success' ? 4000 : 5000);
  };

  const hideAlert = () => {
    setCustomAlert({ show: false, message: '', type: 'error' });
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
    document.documentElement.classList.toggle('minimenu');
    localStorage.setItem('nexel-classic-dashboard-menu-mini-theme', !sidebarCollapsed ? 'menu-mini-theme' : '');
  };

  // Restore sidebar state from localStorage on component mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('nexel-classic-dashboard-menu-mini-theme');
    if (savedTheme === 'menu-mini-theme') {
      setSidebarCollapsed(true);
      document.documentElement.classList.add('minimenu');
    } else {
      setSidebarCollapsed(false);
      document.documentElement.classList.remove('minimenu');
    }
  }, []);

  useEffect(() => {
    const data = authStorage.getLoginData();
    if (!data) return;
    if (authStorage.getDirectLink() === '1') {
      window.location.href = '/';
      return;
    }
    setLoginData(data);
  }, []);

  // Fetch participants
  useEffect(() => {
    dispatch(fetchParticipants());
  }, [dispatch]);

  useEffect(() => {
    if (reduxParticipants) {
      setParticipants(reduxParticipants);
    }
  }, [reduxParticipants]);

  // Fetch upcoming projects
  useEffect(() => {
    if (!loginData) return;
    
    const baseUrl = common.Data.API_URL;
    const url = window.location.href;
    const projectName = url.split('?')[1];
    
    axios.get(`${baseUrl}/v1/get-projects/upcoming-${loginData.id}-${loginData.role}`)
      .then((response) => {
        if (response.data.length > 0) {
          let selProject = response.data[0];
          if (projectName) {
            selProject = response.data.find(x => x.name.toLowerCase() === decodeURIComponent(projectName).toLowerCase());
          }
          setProjects(response.data);
          setOriginalProjects(response.data);
          setSelectedProject(selProject);
          populateInterest(selProject);
        }
      })
      .catch((error) => {
        console.error('Error fetching projects:', error);
        showAlert('Failed to fetch upcoming projects', 'error');
      });
  }, [loginData]);

  // Filter projects based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setProjects(originalProjects);
      return;
    }
    
    const filtered = originalProjects.filter(project => 
      project.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setProjects(filtered);
    
    // If selected project is filtered out, select first filtered project
    if (filtered.length > 0 && selectedProject && !filtered.find(p => p.id === selectedProject.id)) {
      const firstFiltered = filtered[0];
      populateInterest(firstFiltered);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, originalProjects]);

  // Calculate remaining time
  useEffect(() => {
    if (!selectedProject || !selectedProject.edited_on) return;
    
    const updateTimer = () => {
      const projectCreationTime = selectedProject.edited_on;
      const now = new Date().toISOString();
      const timeDiff = Math.abs(new Date(now) - new Date(projectCreationTime));
      const timerHours = parseInt(selectedProject.timer) || 24;
      const remaining = Math.ceil((timerHours * 3600 * 1000 - timeDiff) / 1000);
      setRemainingTime(remaining > 0 ? remaining : 0);
    };
    
    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    
    return () => clearInterval(interval);
  }, [selectedProject]);

  const populateInterest = (project, onComplete) => {
    if (!project) {
      if (onComplete) onComplete();
      return;
    }
    
    const baseUrl = common.Data.API_URL;
    axios.get(`${baseUrl}/v1/get-commitment/${project.id}`)
      .then((response) => {
        setInterests(response.data);
        setSelectedProject(project);
        setAddInterest(false);
        setShowAddInterestBtn(true);
        if (onComplete) onComplete();
      })
      .catch((error) => {
        console.error('Error fetching interests:', error);
        showAlert('Failed to fetch interest details', 'error');
        if (onComplete) onComplete();
      });
  };

  const saveInterest = () => {
    if (!selectedProject) return;
    
    const baseUrl = common.Data.API_URL;
    const formData = {
      ...interestForm,
      proj_id: selectedProject.id,
      part_id: interestForm.part_id || loginData.id
    };
    
    if (!formData.amount || formData.amount === '' || formData.amount === 0 ||
        !formData.wallet_add || formData.wallet_add === '' || formData.wallet_add === 'undefined' ||
        !formData.reason_for_interest || formData.reason_for_interest === '') {
      showAlert('Please fill all mandatory fields', 'error');
      return;
    }
    
    setIsSubmittingInterest(true);
    axios.post(`${baseUrl}/v1/save-interest`, formData)
      .then(() => {
        showAlert('Interest saved successfully', 'success');
        window.location.href = `/upcoming?${selectedProject.name}`;
      })
      .catch((error) => {
        console.error('Error saving interest:', error);
        showAlert('Failed to save interest', 'error');
      });
  };

  const handleAddProject = () => {
    setProjectModalMode('add');
    setEditingProject(null);
    setShowAddProjectModal(true);
    // Reset form
    setProjectForm({
      projectName: '',
      projectURL: '',
      whitepaperURL: '',
      allocation: '',
      allocation_currency: 'USDT',
      tokenomics: '',
      dealanalysis: '',
      fund_wallet: '',
      timer: '24',
      telegram: '',
      poc_email: '',
      whatsapp: '',
      referred: false,
      referred_by: loginData?.id || 0
    });
  };

  const handleEditProject = async (project) => {
    if (!project) return;
    
    setProjectModalMode('edit');
    setEditingProject(project);
    setShowAddProjectModal(true);
    
    // Populate form with project data
    setProjectForm({
      projectName: project.name || '',
      projectURL: project.url || '',
      whitepaperURL: project.whitepaper_url || '',
      allocation: project.allocation_available || '',
      allocation_currency: project.allocation_currency || 'USDT',
      tokenomics: project.tokenomics || '',
      dealanalysis: project.deal_analysis || '',
      fund_wallet: project.fund_wallet_address || '',
      timer: project.timer || '24',
      telegram: project.telegram || '',
      poc_email: project.email || '',
      whatsapp: project.whatsapp || '',
      referred: project.status === 'referred',
      referred_by: project.referred_by || loginData?.id || 0
    });
  };

  // Helper function to process HTML and make links open in new tabs
  const processLinksInHTML = (html) => {
    if (!html) return html;
    // Use a temporary div to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    const links = tempDiv.querySelectorAll('a');
    links.forEach(link => {
      if (!link.getAttribute('target')) {
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noopener noreferrer');
      }
    });
    return tempDiv.innerHTML;
  };

  const handleViewProject = async (project) => {
    if (!project) return;
    
    setProjectModalMode('view');
    setEditingProject(project);
    setShowAddProjectModal(true);
    
    // Process tokenomics and dealanalysis HTML to ensure links open in new tabs
    const processedTokenomics = processLinksInHTML(project.tokenomics || '');
    const processedDealAnalysis = processLinksInHTML(project.deal_analysis || '');
    
    // Populate form with project data
    setProjectForm({
      projectName: project.name || '',
      projectURL: project.url || '',
      whitepaperURL: project.whitepaper_url || '',
      allocation: project.allocation_available || '',
      allocation_currency: project.allocation_currency || 'USDT',
      tokenomics: processedTokenomics,
      dealanalysis: processedDealAnalysis,
      fund_wallet: project.fund_wallet_address || '',
      timer: project.timer || '24',
      telegram: project.telegram || '',
      poc_email: project.email || '',
      whatsapp: project.whatsapp || '',
      referred: project.status === 'referred',
      referred_by: project.referred_by || loginData?.id || 0
    });
  };

  const handleCloseAddProjectModal = () => {
    setShowAddProjectModal(false);
    setProjectModalMode('add');
    setEditingProject(null);
    setProjectForm({
      projectName: '',
      projectURL: '',
      whitepaperURL: '',
      allocation: '',
      allocation_currency: 'USDT',
      tokenomics: '',
      dealanalysis: '',
      fund_wallet: '',
      timer: '24',
      telegram: '',
      poc_email: '',
      whatsapp: '',
      referred: false,
      referred_by: loginData?.id || 0
    });
  };

  const saveProject = async () => {
    if (projectModalMode === 'view') {
      handleCloseAddProjectModal();
      return;
    }

    const { projectName, projectURL, allocation, allocation_currency, fund_wallet, timer, tokenomics, dealanalysis, whitepaperURL, telegram, poc_email, whatsapp, referred, referred_by } = projectForm;
    
    // Validation
    if (!projectName || !projectURL) {
      showAlert('Please fill all mandatory fields (Project Name and Project URL)', 'error');
      return;
    }

    if (!referred && (!allocation || !fund_wallet || !timer)) {
      showAlert('Please fill all mandatory fields (Allocation, Fund Wallet, and Timer)', 'error');
      return;
    }

    setIsSavingProject(true);
    try {
      const baseUrl = common.Data.API_URL;
      const body = {
        project_name: projectName,
        project_url: projectURL,
        whitepaper_url: whitepaperURL || '',
        status: referred ? 'referred' : 'upcoming',
        allocation: allocation || 0,
        allocation_currency: referred ? '' : (allocation_currency || 'USDT'),
        tokenomics: tokenomics || '',
        deal_analysis: dealanalysis || '',
        referred_by: referred ? (referred_by || loginData?.id) : 0,
        fund_wallet: fund_wallet || '',
        timer: timer || '24',
        referred: referred ? 1 : 0,
        telegram: telegram || '',
        email: poc_email || '',
        whatsapp: whatsapp || '',
        created_by: loginData?.id
      };

      if (projectModalMode === 'edit' && editingProject) {
        body.project_id = editingProject.id;
        await axios.post(`${baseUrl}/v1/update-project`, body);
        showAlert('Project updated successfully!', 'success');
      } else {
        await axios.post(`${baseUrl}/v1/save-project`, body);
        showAlert('Project saved successfully!', 'success');
      }
      
      handleCloseAddProjectModal();
      
      // Refresh projects list
      if (loginData) {
        const url = window.location.href;
        const projectNameParam = url.split('?')[1];
        const projectResponse = await axios.get(`${baseUrl}/v1/get-projects/upcoming-${loginData.id}-${loginData.role}`);
        const projectsData = projectResponse.data || [];
        setProjects(projectsData);
        
        if (projectNameParam) {
          const selected = projectsData.find(p => p.name === projectNameParam);
          if (selected) {
            populateInterest(selected);
          }
        }
      }
    } catch (error) {
      console.error('Error saving project:', error);
      showAlert(error.response?.data?.message || 'Failed to save project', 'error');
    } finally {
      setIsSavingProject(false);
    }
  };

  const handleAddInterest = (interest) => {
    if (interest) {
      setInterestForm({
        proj_id: interest.proj_id,
        part_id: interest.participant_id,
        amount: interest.amount,
        wallet_add: interest.wallet_address || '',
        reason_for_interest: interest.reason_for_interest || ''
      });
    } else {
      setInterestForm({
        proj_id: selectedProject?.id || 0,
        part_id: loginData?.id || 0,
        amount: '',
        wallet_add: '',
        reason_for_interest: ''
      });
    }
    setAddInterest(true);
    setShowAddInterestBtn(false);
  };

  const goBack = () => {
    setAddInterest(false);
    setShowAddInterestBtn(true);
  };

  // Calculate stats
  const calculateStats = () => {
    if (!selectedProject) {
      return {
        totalAllocation: 0,
        participants: 0,
        totalCommitment: 0,
        allocationAvailable: 0
      };
    }
    
    const totalAllocation = selectedProject.allocation_available ? parseInt(selectedProject.allocation_available) : 0;
    let totalCommitment = 0;
    
    interests.forEach(interest => {
      totalCommitment += parseInt(interest.amount || 0);
    });
    
    const allocationAvailable = totalAllocation - totalCommitment;
    const participants = interests.length;
    
    return {
      totalAllocation,
      participants,
      totalCommitment,
      allocationAvailable
    };
  };

  const stats = calculateStats();
  const isSpecialProject = selectedProject?.is_special && 
                          !(selectedProject.special_participants && selectedProject.special_participants.split(',').includes(loginData?.id + '')) && 
                          loginData?.role != 1;

  const formatTime = (seconds) => {
    if (seconds <= 0) return '00:00:00';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  return (
    <div className="nxl-wrapper">
      {/* Custom Alert */}
      {customAlert.show && (
        <div 
          className="position-fixed"
          style={{
            top: '24px',
            right: '24px',
            zIndex: 9999,
            minWidth: '380px',
            maxWidth: '450px',
            animation: 'slideInRight 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55)'
          }}
        >
          <div 
            className="card shadow-lg border-0"
            style={{
              borderRadius: '16px',
              overflow: 'hidden',
              border: `2px solid ${
                customAlert.type === 'error' ? '#dc3545' : 
                customAlert.type === 'success' ? '#28a745' : 
                customAlert.type === 'warning' ? '#ffc107' : 
                '#17a2b8'
              }`
            }}
          >
            <div 
              className={`card-body p-3 d-flex align-items-center position-relative`}
              style={{
                backgroundColor: '#ffffff',
                borderLeft: `5px solid ${
                  customAlert.type === 'error' ? '#dc3545' : 
                  customAlert.type === 'success' ? '#28a745' : 
                  customAlert.type === 'warning' ? '#ffc107' : 
                  '#17a2b8'
                }`
              }}
            >
              <div 
                className={`d-flex align-items-center justify-content-center me-3`}
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  backgroundColor: customAlert.type === 'error' ? 'rgba(220, 53, 69, 0.15)' : 
                                   customAlert.type === 'success' ? 'rgba(40, 167, 69, 0.15)' : 
                                   customAlert.type === 'warning' ? 'rgba(255, 193, 7, 0.15)' : 
                                   'rgba(23, 162, 184, 0.15)',
                  flexShrink: 0,
                  boxShadow: `0 2px 8px rgba(0,0,0,0.08)`
                }}
              >
                <i 
                  className={`feather-${
                    customAlert.type === 'error' ? 'alert-circle' : 
                    customAlert.type === 'success' ? 'check-circle' : 
                    customAlert.type === 'warning' ? 'alert-triangle' : 
                    'info'
                  }`}
                  style={{ 
                    fontSize: '24px', 
                    color: customAlert.type === 'error' ? '#dc3545' : 
                           customAlert.type === 'success' ? '#28a745' : 
                           customAlert.type === 'warning' ? '#ffc107' : 
                           '#17a2b8'
                  }}
                ></i>
              </div>
              <div className="flex-grow-1">
                <h6 
                  className={`mb-1 fw-bold`}
                  style={{ fontSize: '16px', 
                           color: customAlert.type === 'error' ? '#dc3545' : 
                                  customAlert.type === 'success' ? '#28a745' : 
                                  customAlert.type === 'warning' ? '#ffc107' : 
                                  '#17a2b8'
                         }}
                >
                  {customAlert.type === 'error' ? 'Error' : 
                   customAlert.type === 'success' ? 'Success' : 
                   customAlert.type === 'warning' ? 'Warning' : 
                   'Information'}
                </h6>
                <p className="mb-0 text-dark" style={{ fontSize: '14px', lineHeight: '1.4' }}>
                  {customAlert.message}
                </p>
              </div>
              <button
                type="button"
                className="btn-close ms-2"
                onClick={hideAlert}
                aria-label="Close"
                style={{
                  opacity: 0.8,
                  transition: 'all 0.2s ease',
                  transform: 'scale(1)'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              ></button>
            </div>
            <div 
              className="progress" 
              style={{ height: '4px', borderRadius: '0', backgroundColor: 'rgba(0,0,0,0.05)' }}
            >
              <div 
                className={`progress-bar ${
                  customAlert.type === 'error' ? 'bg-danger' : 
                  customAlert.type === 'success' ? 'bg-success' : 
                  customAlert.type === 'warning' ? 'bg-warning' : 
                  'bg-info'
                }`}
                role="progressbar" 
                style={{ width: '100%', animation: `progressBar ${customAlert.type === 'success' ? 4 : 5}s linear forwards` }} 
                aria-valuenow="100" 
                aria-valuemin="0" 
                aria-valuemax="100"
              ></div>
            </div>
          </div>
        </div>
      )}
      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%) scale(0.9);
            opacity: 0;
          }
          to {
            transform: translateX(0) scale(1);
            opacity: 1;
          }
        }
        @keyframes progressBar {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>

      <Sidebar 
        sidebarCollapsed={sidebarCollapsed} 
        setSidebarCollapsed={setSidebarCollapsed} 
        toggleSidebar={toggleSidebar}
      />

      {/* Main Content */}
      <main className="nxl-container" style={{ paddingTop: '0' }}>
        <div className="nxl-content" style={{ paddingTop: '0' }}>
          {/* Page Header */}
          <div className="page-header">
            <div className="page-header-left d-flex align-items-center">
              {/* Sidebar Toggle Button */}
              <div className="nxl-navigation-toggle me-3">
                <a href="javascript:void(0);" id="menu-mini-button" onClick={toggleSidebar} style={{ display: sidebarCollapsed ? 'none' : 'block' }}>
                  <i className="feather-align-left"></i>
                </a>
                <a href="javascript:void(0);" id="menu-expend-button" onClick={toggleSidebar} style={{ display: sidebarCollapsed ? 'block' : 'none' }}>
                  <i className="feather-arrow-right"></i>
                </a>
              </div>
              <div className="page-header-title">
                <h5 className="m-b-10">Upcoming Projects</h5>
              </div>
              <ul className="breadcrumb">
                <li className="breadcrumb-item"><a href="/investor-dashboard">Home</a></li>
                <li className="breadcrumb-item">Upcoming Projects</li>
              </ul>
            </div>
            <div className="page-header-right ms-auto d-flex align-items-center gap-3">
              {/* Search Bar */}
              <div className="position-relative" style={{ width: '200px' }}>
                <i className="feather-search position-absolute" style={{ left: '8px', top: '50%', transform: 'translateY(-50%)', color: '#6c757d', fontSize: '14px', pointerEvents: 'none' }}></i>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Search project"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    paddingLeft: '32px',
                    paddingRight: searchTerm ? '28px' : '12px',
                    fontSize: '12px',
                    borderColor: '#dee2e6',
                    borderRadius: '6px',
                    height: '32px'
                  }}
                />
                {searchTerm && (
                  <button
                    type="button"
                    className="btn-close position-absolute"
                    onClick={() => setSearchTerm('')}
                    style={{
                      right: '6px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      fontSize: '10px',
                      opacity: 0.6,
                      width: '16px',
                      height: '16px'
                    }}
                    aria-label="Clear search"
                  ></button>
                )}
              </div>
              {loginData && (
                <div className="d-flex align-items-center gap-2">
                  <i className="feather-user text-primary"></i>
                  <span className="fw-semibold text-dark" style={{ fontSize: '14px' }}>
                    Welcome, {(() => {
                      if (participants.length > 0 && loginData.id) {
                        const userParticipant = participants.find(p => p.participant_id == loginData.id || p.id == loginData.id);
                        return userParticipant?.name || loginData.name || 'User';
                      }
                      return loginData.name || 'User';
                    })()}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="container-fluid py-4">

          {/* Project Navigation Tabs */}
          <div className="card shadow-sm border-0 mb-4">
            <div className="card-body p-0">
              <ul className="nav nav-tabs border-0" style={{ 
                padding: '0.5rem 1rem',
                borderBottom: '1px solid #dee2e6',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '0.25rem'
              }}>
                {loginData?.role == 1 && (
                  <li className="nav-item">
                    <button
                      type="button"
                      className="nav-link"
                      onClick={handleAddProject}
                      style={{
                        padding: '0.625rem 1.25rem',
                        color: '#495057',
                        fontWeight: '600',
                        fontSize: '13px',
                        borderRadius: '0',
                        borderBottom: '3px solid transparent',
                        transition: 'all 0.3s ease',
                        textDecoration: 'none',
                        border: 'none',
                        background: 'none',
                        width: '100%',
                        textAlign: 'left'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = '#0d6efd';
                        e.currentTarget.style.backgroundColor = 'rgba(13, 110, 253, 0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = '#495057';
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <i className="feather-plus me-1"></i>Add New Project
                    </button>
                  </li>
                )}
                {projects.map(project => {
                  const isVisible = !project.is_special || 
                                   (project.special_participants && project.special_participants.split(',').includes(loginData?.id + '')) || 
                                   loginData?.role == 1;
                  if (!isVisible) return null;
                  
                  const isActive = selectedProject?.id === project.id;
                  
                  return (
                    <li key={project.id} className="nav-item">
                      <button
                        className={`nav-link ${isActive ? 'active' : ''}`}
                        onClick={() => populateInterest(project)}
                        style={{ 
                          border: 'none',
                          background: isActive ? 'rgba(13, 110, 253, 0.1)' : 'none',
                          cursor: 'pointer',
                          padding: '0.625rem 1.25rem',
                          color: isActive ? '#0d6efd' : '#495057',
                          fontWeight: '600',
                          fontSize: '13px',
                          borderRadius: '0',
                          borderBottom: isActive ? '3px solid #0d6efd' : '3px solid transparent',
                          transition: 'all 0.3s ease',
                          position: 'relative'
                        }}
                        onMouseEnter={(e) => {
                          if (!isActive) {
                            e.currentTarget.style.color = '#0d6efd';
                            e.currentTarget.style.backgroundColor = 'rgba(13, 110, 253, 0.05)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isActive) {
                            e.currentTarget.style.color = '#495057';
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        {project.name}
                        {(project.reco_done || project.recommendation_done) && (
                          <span 
                            style={{
                              position: 'absolute',
                              top: '4px',
                              right: '4px',
                              width: '8px',
                              height: '8px',
                              backgroundColor: '#28a745',
                              borderRadius: '50%',
                              boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
                            }}
                          ></span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>

          {/* Timer Banner */}
          {selectedProject && (
            <div 
              className="mb-4 border rounded d-flex align-items-center gap-2"
              style={{
                backgroundColor: remainingTime > 0 ? '#ffffff' : '#fff8f0',
                borderColor: remainingTime > 0 ? '#dee2e6' : '#ffc107',
                borderWidth: remainingTime > 0 ? '1px' : '1.5px',
                borderLeftWidth: remainingTime > 0 ? '1px' : '4px',
                borderLeftColor: remainingTime > 0 ? '#dee2e6' : '#ffc107',
                padding: '0.75rem 1rem'
              }}
            >
              <i 
                className={`feather-${remainingTime > 0 ? 'clock' : 'alert-triangle'}`} 
                style={{ 
                  fontSize: '16px',
                  color: remainingTime > 0 ? '#0d6efd' : '#ffc107',
                  flexShrink: 0
                }}
              ></i>
              <span style={{ fontSize: '13px', color: remainingTime > 0 ? '#6c757d' : '#856404', fontWeight: '500' }}>
                {remainingTime > 0 ? 'Allocation Interest Deadline:' : 'Interest Submission Closed:'}
              </span>
              {remainingTime > 0 ? (
                <>
                  <span style={{ fontFamily: 'monospace', letterSpacing: '0.5px', color: '#0d6efd', fontWeight: '600', fontSize: '14px' }}>
                    {formatTime(remainingTime)}
                  </span>
                  <span className="text-muted" style={{ fontSize: '12px' }}>remaining</span>
                </>
              ) : (
                <span style={{ fontSize: '13px', color: '#856404', fontWeight: '600' }}>Time expired</span>
              )}
              <span className="ms-auto text-muted" style={{ fontSize: '12px' }}>
                {remainingTime > 0 ? 'Submit within 24 hours of project upload' : '24-hour window ended'}
              </span>
            </div>
          )}

          {/* Stats Cards */}
          {selectedProject && !isSpecialProject && (
            <div className="row mb-4">
              <div className="col-sm-6 col-lg-3 mb-3">
                <div className="card shadow-sm border-0 h-100">
                  <div className="card-body">
                    <div className="d-flex align-items-center gap-3">
                      <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px', backgroundColor: '#e3f2fd' }}>
                        <i className="feather-dollar-sign text-primary" style={{ fontSize: '20px' }}></i>
                      </div>
                      <div>
                        <div className="text-muted mb-1" style={{ fontSize: '12px', fontWeight: '500' }}>Total Allocation</div>
                        <div className="fw-bold text-dark" style={{ fontSize: '18px' }}>
                          {stats.totalAllocation.toLocaleString()} {selectedProject.allocation_currency || 'USDT'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-sm-6 col-lg-3 mb-3">
                <div className="card shadow-sm border-0 h-100">
                  <div className="card-body">
                    <div className="d-flex align-items-center gap-3">
                      <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px', backgroundColor: '#e8f5e9' }}>
                        <i className="feather-users text-success" style={{ fontSize: '20px' }}></i>
                      </div>
                      <div>
                        <div className="text-muted mb-1" style={{ fontSize: '12px', fontWeight: '500' }}>Participants Interested</div>
                        <div className="fw-bold text-dark" style={{ fontSize: '18px' }}>{stats.participants}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-sm-6 col-lg-3 mb-3">
                <div className="card shadow-sm border-0 h-100">
                  <div className="card-body">
                    <div className="d-flex align-items-center gap-3">
                      <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px', backgroundColor: '#fff3e0' }}>
                        <i className="feather-trending-up text-warning" style={{ fontSize: '20px' }}></i>
                      </div>
                      <div>
                        <div className="text-muted mb-1" style={{ fontSize: '12px', fontWeight: '500' }}>Allocation Used</div>
                        <div className="fw-bold text-warning" style={{ fontSize: '18px' }}>
                          {stats.totalCommitment.toLocaleString()} {selectedProject.allocation_currency || 'USDT'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-sm-6 col-lg-3 mb-3">
                <div className="card shadow-sm border-0 h-100">
                  <div className="card-body">
                    <div className="d-flex align-items-center gap-3">
                      <div className={`rounded-circle d-flex align-items-center justify-content-center`} 
                           style={{ 
                             width: '48px', 
                             height: '48px', 
                             backgroundColor: stats.allocationAvailable <= 0 ? '#ffebee' : '#e8f5e9'
                           }}>
                        <i className={`feather-${stats.allocationAvailable <= 0 ? 'alert-triangle' : 'check-circle'}`} 
                           style={{ 
                             fontSize: '20px',
                             color: stats.allocationAvailable <= 0 ? '#dc3545' : '#28a745'
                           }}></i>
                      </div>
                      <div>
                        <div className="text-muted mb-1" style={{ fontSize: '12px', fontWeight: '500' }}>
                          {stats.allocationAvailable <= 0 ? 'Oversubscribed' : 'Allocation Available'}
                        </div>
                        <div className={`fw-bold ${stats.allocationAvailable <= 0 ? 'text-danger' : 'text-success'}`} style={{ fontSize: '18px' }}>
                          {stats.allocationAvailable.toLocaleString()} {selectedProject.allocation_currency || 'USDT'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          {selectedProject && selectedProject.name && (
            <div className="card shadow-sm border-0 mb-4">
              <div className="card-body">
                <div className="d-flex gap-2 flex-wrap">
                  {(remainingTime > 0 || loginData?.id == 1) && (
                    <button 
                      type="button" 
                      className="btn btn-primary btn-sm"
                      onClick={() => handleAddInterest(null)}
                    >
                      <i className="feather-plus me-1"></i>Add Interest
                    </button>
                  )}
                  {loginData?.role == 1 && (
                    <button 
                      type="button" 
                      className="btn btn-danger btn-sm"
                      onClick={() => handleEditProject(selectedProject)}
                    >
                      <i className="feather-edit me-1"></i>Edit {selectedProject.name}
                    </button>
                  )}
                  <button 
                    type="button" 
                    className="btn btn-warning btn-sm"
                    onClick={() => handleViewProject(selectedProject)}
                    disabled={isSpecialProject}
                  >
                    <i className="feather-eye me-1"></i>View {selectedProject.name}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Main Content Card */}
          <div className="card shadow-sm border-0">
            <div className="card-header bg-white border-bottom d-flex justify-content-between align-items-center">
              <h6 className="mb-0 fw-bold">
                {!addInterest ? 'Allocation Interest Details' : 'Add Interest'}
              </h6>
              {showAddInterestBtn && loginData?.role == 1 && selectedProject?.name && remainingTime <= 0 && interests.length > 0 && (
                <button 
                  type="button" 
                  className="btn btn-success btn-sm"
                  onClick={() => window.location.href = `/allocation-sheet?project=${selectedProject.id}`}
                >
                  <i className="feather-check me-1"></i>Proceed to Allocation Sheet
                </button>
              )}
            </div>
            <div className="card-body">
              {!addInterest ? (
                <div className="table-responsive" style={{ overflowX: 'auto', maxWidth: '100%' }}>
                  <table className="table table-hover mb-0" style={{ width: '100%', tableLayout: 'fixed' }}>
                    <thead style={{ backgroundColor: '#f8f9fa' }}>
                      <tr>
                        <th className="border-0 px-2 py-2 fw-semibold text-dark fs-12 text-center" style={{ width: '6%' }}>S. No.</th>
                        <th className="border-0 px-2 py-2 fw-semibold text-dark fs-12 text-center" style={{ width: '12%' }}>Name</th>
                        <th className="border-0 px-2 py-2 fw-semibold text-dark fs-12 text-center" style={{ width: '8%' }}>Currency</th>
                        <th className="border-0 px-2 py-2 fw-semibold text-dark fs-12 text-center" style={{ width: '10%' }}>Amount</th>
                        <th className="border-0 px-2 py-2 fw-semibold text-dark fs-12 text-center" style={{ width: '22%' }}>Wallet Address</th>
                        <th className="border-0 px-2 py-2 fw-semibold text-dark fs-12 text-center" style={{ width: '22%' }}>Reason for Interest</th>
                        <th className="border-0 px-2 py-2 fw-semibold text-dark fs-12 text-center" style={{ width: '20%' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {interests.map((interest, index) => {
                        if ((interest.participant_id == loginData?.id) || loginData?.role == 1) {
                          return (
                            <tr key={index} className="border-bottom">
                              <td className="px-3 py-2 text-center">
                                <span className="fw-semibold text-dark fs-12">{index + 1}</span>
                              </td>
                              <td className="px-3 py-2 text-center">
                                <span className="fw-medium text-dark fs-12">{interest.participant_name}</span>
                              </td>
                              <td className="px-3 py-2 text-center">
                                <span className="fw-medium text-dark fs-12">{selectedProject?.allocation_currency || 'USDT'}</span>
                              </td>
                              <td className="px-3 py-2 text-center">
                                <span className="fw-semibold text-dark fs-12">{interest.amount}</span>
                              </td>
                              <td className="px-3 py-2 text-center">
                                <span className="fw-medium text-dark fs-12" style={{ wordBreak: 'break-all' }}>
                                  {interest.wallet_address && interest.wallet_address !== 'undefined' ? interest.wallet_address : ''}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-center">
                                <span className="fw-medium text-dark fs-12">
                                  {interest.reason_for_interest && interest.reason_for_interest !== 'undefined' ? interest.reason_for_interest : ''}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-center">
                                {((interest.participant_id == loginData?.id && remainingTime > 0) || loginData?.id == 1) && (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => handleAddInterest(interest)}
                                    title="Edit Interest"
                                  >
                                    <i className="feather-edit fs-12"></i>
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        }
                        return null;
                      })}
                      {interests.length === 0 && (
                        <tr>
                          <td colSpan="7" className="text-center py-4 text-muted">
                            <i className="feather-inbox fs-1 mb-2 d-block"></i>
                            No interest details found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="row">
                  <div className="col-lg-12">
                    <div className="card border-0" style={{ backgroundColor: '#f8f9fa' }}>
                      <div className="card-body" style={{ paddingTop: '0.75rem', paddingBottom: '0.75rem', paddingLeft: '1rem', paddingRight: '1rem' }}>
                        <h6 className="fw-bold mb-3" style={{ fontSize: '14px' }}>
                          <i className="feather-edit me-2"></i>Allocation Interest Form
                        </h6>
                        <form>
                          <div className="row mb-3">
                            <label className="col-md-3 col-form-label fw-semibold text-dark fs-13">
                              Participant Name <span className="text-danger">*</span>
                            </label>
                            <div className="col-md-6">
                              <select
                                className="form-select form-select-sm"
                                value={interestForm.part_id || loginData?.id || ''}
                                disabled={loginData?.id != 1}
                                onChange={(e) => setInterestForm({ ...interestForm, part_id: parseInt(e.target.value) })}
                              >
                                {participants.map(pcpt => (
                                  <option key={pcpt.id} value={pcpt.id}>{pcpt.name}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <div className="row mb-3">
                            <label className="col-md-3 col-form-label fw-semibold text-dark fs-13">
                              Allocation Currency <span className="text-danger">*</span>
                            </label>
                            <div className="col-md-6">
                              <select
                                className="form-select form-select-sm"
                                value={selectedProject?.allocation_currency || 'USDT'}
                                disabled
                              >
                                <option value="USDT">USDT</option>
                                <option value="USDC">USDC</option>
                                <option value="ETH">Ethereum</option>
                                <option value="DAI">DAI</option>
                              </select>
                            </div>
                          </div>
                          <div className="row mb-3">
                            <label className="col-md-3 col-form-label fw-semibold text-dark fs-13">
                              Amount <span className="text-danger">*</span>
                            </label>
                            <div className="col-md-6">
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder="0.00"
                                value={interestForm.amount}
                                onChange={(e) => setInterestForm({ ...interestForm, amount: e.target.value })}
                              />
                            </div>
                          </div>
                          <div className="row mb-3">
                            <label className="col-md-3 col-form-label fw-semibold text-dark fs-13">
                              Wallet Address <span className="text-danger">*</span>
                            </label>
                            <div className="col-md-6">
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder="Enter wallet address"
                                value={interestForm.wallet_add !== 'undefined' ? interestForm.wallet_add : ''}
                                onChange={(e) => setInterestForm({ ...interestForm, wallet_add: e.target.value })}
                              />
                            </div>
                          </div>
                          <div className="row mb-3">
                            <label className="col-md-3 col-form-label fw-semibold text-dark fs-13">
                              Reason for Interest <span className="text-danger">*</span>
                            </label>
                            <div className="col-md-6">
                              <textarea
                                className="form-control form-control-sm"
                                rows="6"
                                placeholder="Please provide reason for your interest"
                                value={interestForm.reason_for_interest}
                                onChange={(e) => setInterestForm({ ...interestForm, reason_for_interest: e.target.value })}
                              ></textarea>
                            </div>
                          </div>
                          <div className="row">
                            <div className="col-md-9 offset-md-3">
                              <div className="d-flex gap-2">
                                <button
                                  type="button"
                                  className="btn btn-light btn-sm"
                                  onClick={goBack}
                                  style={{
                                    borderRadius: '8px',
                                    padding: '8px 20px',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                    border: '1px solid #dee2e6'
                                  }}
                                >
                                  <i className="feather-x me-1"></i>Cancel
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-primary btn-sm"
                                  onClick={saveInterest}
                                  disabled={isSubmittingInterest}
                                  style={{
                                    borderRadius: '8px',
                                    padding: '8px 24px',
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    opacity: isSubmittingInterest ? 0.6 : 1,
                                    cursor: isSubmittingInterest ? 'not-allowed' : 'pointer'
                                  }}
                                >
                                  {isSubmittingInterest ? (
                                    <>
                                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                      Submitting...
                                    </>
                                  ) : (
                                    <>
                                      <i className="feather-save me-1"></i>Submit
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          </div>
        </div>
      </main>

      {/* Right Sidebar Modal for Add New Project */}
        {showAddProjectModal && (
          <>
            {/* Backdrop */}
            <div
              className="position-fixed"
              style={{
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 1040,
                animation: 'fadeIn 0.3s ease'
              }}
              onClick={handleCloseAddProjectModal}
            />
            
            {/* Sidebar Modal */}
            <div
              className="position-fixed bg-white shadow-lg"
              style={{
                top: 0,
                right: 0,
                bottom: 0,
                width: '600px',
                maxWidth: '90vw',
                zIndex: 1050,
                overflowY: 'auto',
                animation: 'slideInRight 0.3s ease',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              {/* Modal Header */}
              <div
                className="border-bottom bg-white"
                style={{
                  padding: '1.5rem',
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0 fw-bold">
                    <i className={`feather-${projectModalMode === 'add' ? 'plus' : projectModalMode === 'edit' ? 'edit' : 'eye'} me-2 text-primary`}></i>
                    {projectModalMode === 'add' ? 'Add New Project' : projectModalMode === 'edit' ? 'Edit Project' : 'View Project'}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleCloseAddProjectModal}
                    aria-label="Close"
                  ></button>
                </div>
              </div>

              {/* Modal Body */}
              <div style={{ padding: '1.5rem', flex: 1 }}>
                <form onSubmit={(e) => { e.preventDefault(); saveProject(); }}>
                  <div className="row">
                    {/* Project Name */}
                    <div className="col-12 mb-3">
                      <label className="form-label fw-semibold mb-2 text-dark fs-13">
                        <i className="feather-file-text me-2 text-primary fs-12"></i>
                        Project Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter project name"
                        value={projectForm.projectName}
                        onChange={(e) => setProjectForm({ ...projectForm, projectName: e.target.value })}
                        required
                        disabled={projectModalMode === 'view'}
                        style={{ borderColor: '#dee2e6', fontSize: '13px', backgroundColor: projectModalMode === 'view' ? '#f8f9fa' : 'white' }}
                      />
                    </div>

                    {/* Project URL */}
                    <div className="col-12 mb-3">
                      <label className="form-label fw-semibold mb-2 text-dark fs-13">
                        <i className="feather-link me-2 text-primary fs-12"></i>
                        Project URL <span className="text-danger">*</span>
                      </label>
                      <input
                        type="url"
                        className="form-control"
                        placeholder="Enter project website URL"
                        value={projectForm.projectURL}
                        onChange={(e) => setProjectForm({ ...projectForm, projectURL: e.target.value })}
                        required
                        disabled={projectModalMode === 'view'}
                        style={{ borderColor: '#dee2e6', fontSize: '13px', backgroundColor: projectModalMode === 'view' ? '#f8f9fa' : 'white' }}
                      />
                    </div>

                    {/* Whitepaper URL */}
                    <div className="col-12 mb-3">
                      <label className="form-label fw-semibold mb-2 text-dark fs-13">
                        <i className="feather-book me-2 text-primary fs-12"></i>
                        Whitepaper URL
                      </label>
                      <input
                        type="url"
                        className="form-control"
                        placeholder="Enter whitepaper URL"
                        value={projectForm.whitepaperURL}
                        onChange={(e) => setProjectForm({ ...projectForm, whitepaperURL: e.target.value })}
                        disabled={projectModalMode === 'view'}
                        style={{ borderColor: '#dee2e6', fontSize: '13px', backgroundColor: projectModalMode === 'view' ? '#f8f9fa' : 'white' }}
                      />
                    </div>

                    {/* Allocation Currency */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-semibold mb-2 text-dark fs-13">
                        <i className="feather-dollar-sign me-2 text-primary fs-12"></i>
                        Allocation Currency <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-select"
                        value={projectForm.allocation_currency}
                        onChange={(e) => setProjectForm({ ...projectForm, allocation_currency: e.target.value })}
                        required={!projectForm.referred}
                        disabled={projectForm.referred || projectModalMode === 'view'}
                        style={{ borderColor: '#dee2e6', fontSize: '13px', backgroundColor: (projectForm.referred || projectModalMode === 'view') ? '#f8f9fa' : 'white' }}
                      >
                        <option value="USDT">USDT</option>
                        <option value="USDC">USDC</option>
                        <option value="ETH">Ethereum</option>
                        <option value="DAI">DAI</option>
                      </select>
                    </div>

                    {/* Allocation Available */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-semibold mb-2 text-dark fs-13">
                        <i className="feather-trending-up me-2 text-primary fs-12"></i>
                        Allocation Available <span className="text-danger">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Enter allocation amount"
                        value={projectForm.allocation}
                        onChange={(e) => setProjectForm({ ...projectForm, allocation: e.target.value })}
                        required={!projectForm.referred}
                        disabled={projectForm.referred || projectModalMode === 'view'}
                        style={{ borderColor: '#dee2e6', fontSize: '13px', backgroundColor: (projectForm.referred || projectModalMode === 'view') ? '#f8f9fa' : 'white' }}
                      />
                    </div>

                    {/* Fund Wallet */}
                    <div className="col-12 mb-3">
                      <label className="form-label fw-semibold mb-2 text-dark fs-13">
                        <i className="feather-wallet me-2 text-primary fs-12"></i>
                        Fund Wallet Address <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter fund wallet address"
                        value={projectForm.fund_wallet}
                        onChange={(e) => setProjectForm({ ...projectForm, fund_wallet: e.target.value })}
                        required={!projectForm.referred}
                        disabled={projectForm.referred || projectModalMode === 'view'}
                        style={{ borderColor: '#dee2e6', fontSize: '13px', backgroundColor: (projectForm.referred || projectModalMode === 'view') ? '#f8f9fa' : 'white' }}
                      />
                    </div>

                    {/* Timer */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-semibold mb-2 text-dark fs-13">
                        <i className="feather-clock me-2 text-primary fs-12"></i>
                        Timer (hours) <span className="text-danger">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="24"
                        value={projectForm.timer}
                        onChange={(e) => setProjectForm({ ...projectForm, timer: e.target.value })}
                        required={!projectForm.referred}
                        disabled={projectForm.referred || projectModalMode === 'view'}
                        style={{ borderColor: '#dee2e6', fontSize: '13px', backgroundColor: (projectForm.referred || projectModalMode === 'view') ? '#f8f9fa' : 'white' }}
                      />
                    </div>

                    {/* Telegram */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-semibold mb-2 text-dark fs-13">
                        <i className="feather-send me-2 text-primary fs-12"></i>
                        Telegram
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter telegram link"
                        value={projectForm.telegram}
                        onChange={(e) => setProjectForm({ ...projectForm, telegram: e.target.value })}
                        disabled={projectModalMode === 'view'}
                        style={{ borderColor: '#dee2e6', fontSize: '13px', backgroundColor: projectModalMode === 'view' ? '#f8f9fa' : 'white' }}
                      />
                    </div>

                    {/* POC Email */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-semibold mb-2 text-dark fs-13">
                        <i className="feather-mail me-2 text-primary fs-12"></i>
                        POC Email
                      </label>
                      <input
                        type="email"
                        className="form-control"
                        placeholder="Enter concerned person email"
                        value={projectForm.poc_email}
                        onChange={(e) => setProjectForm({ ...projectForm, poc_email: e.target.value })}
                        disabled={projectModalMode === 'view'}
                        style={{ borderColor: '#dee2e6', fontSize: '13px', backgroundColor: projectModalMode === 'view' ? '#f8f9fa' : 'white' }}
                      />
                    </div>

                    {/* WhatsApp */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-semibold mb-2 text-dark fs-13">
                        <i className="feather-phone me-2 text-primary fs-12"></i>
                        WhatsApp No.
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter WhatsApp number"
                        value={projectForm.whatsapp}
                        onChange={(e) => setProjectForm({ ...projectForm, whatsapp: e.target.value })}
                        disabled={projectModalMode === 'view'}
                        style={{ borderColor: '#dee2e6', fontSize: '13px', backgroundColor: projectModalMode === 'view' ? '#f8f9fa' : 'white' }}
                      />
                    </div>

                    {/* Tokenomics/Deal Details */}
                    <div className="col-12 mb-3">
                      <label className="form-label fw-semibold mb-2 text-dark fs-13">
                        <i className="feather-file-text me-2 text-primary fs-12"></i>
                        Tokenomics/Deal Details
                      </label>
                      {projectModalMode === 'view' ? (
                        <div 
                          className="form-control ck-content-view"
                          style={{ 
                            borderColor: '#dee2e6', 
                            fontSize: '13px', 
                            backgroundColor: '#f8f9fa',
                            minHeight: '200px',
                            padding: '12px',
                            overflow: 'auto'
                          }}
                          dangerouslySetInnerHTML={{ __html: projectForm.tokenomics || '<p class="text-muted">No content</p>' }}
                        />
                      ) : (
                        <div style={{ border: '1px solid #dee2e6', borderRadius: '0.375rem' }}>
                          <CKEditor
                            editor={ClassicEditor}
                            data={projectForm.tokenomics || ''}
                            disabled={projectModalMode === 'view'}
                            onReady={(editor) => {
                              editor.editing.view.change((writer) => {
                                writer.setStyle('min-height', '200px', editor.editing.view.document.getRoot());
                              });
                            }}
                            onChange={(event, editor) => {
                              const data = editor.getData();
                              setProjectForm({ ...projectForm, tokenomics: data });
                            }}
                            config={{
                              toolbar: ['heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList', '|', 'blockQuote', 'insertTable', '|', 'undo', 'redo']
                            }}
                          />
                        </div>
                      )}
                    </div>

                    {/* Deal Analysis */}
                    {!projectForm.referred && (
                      <div className="col-12 mb-3">
                        <label className="form-label fw-semibold mb-2 text-dark fs-13">
                          <i className="feather-bar-chart-2 me-2 text-primary fs-12"></i>
                          Deal Analysis
                        </label>
                        {projectModalMode === 'view' ? (
                          <div 
                            className="form-control ck-content-view"
                            style={{ 
                              borderColor: '#dee2e6', 
                              fontSize: '13px', 
                              backgroundColor: '#f8f9fa',
                              minHeight: '200px',
                              padding: '12px',
                              overflow: 'auto'
                            }}
                            dangerouslySetInnerHTML={{ __html: projectForm.dealanalysis || '<p class="text-muted">No content</p>' }}
                          />
                        ) : (
                          <div style={{ border: '1px solid #dee2e6', borderRadius: '0.375rem' }}>
                            <CKEditor
                              editor={ClassicEditor}
                              data={projectForm.dealanalysis || ''}
                              disabled={projectModalMode === 'view'}
                              onReady={(editor) => {
                                editor.editing.view.change((writer) => {
                                  writer.setStyle('min-height', '200px', editor.editing.view.document.getRoot());
                                });
                              }}
                              onChange={(event, editor) => {
                                const data = editor.getData();
                                setProjectForm({ ...projectForm, dealanalysis: data });
                              }}
                              config={{
                                toolbar: ['heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList', '|', 'blockQuote', 'insertTable', '|', 'undo', 'redo']
                              }}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </form>
              </div>

              {/* Modal Footer */}
              <div
                className="border-top bg-white"
                style={{
                  padding: '1.5rem',
                  position: 'sticky',
                  bottom: 0,
                  zIndex: 10,
                  boxShadow: '0 -2px 4px rgba(0,0,0,0.1)'
                }}
              >
                <div className="d-flex gap-2 justify-content-end">
                  <button
                    type="button"
                    className="btn btn-light btn-sm"
                    onClick={handleCloseAddProjectModal}
                    disabled={isSavingProject}
                    style={{
                      borderRadius: '8px',
                      padding: '8px 20px',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}
                  >
                    <i className="feather-x me-1"></i>{projectModalMode === 'view' ? 'Close' : 'Cancel'}
                  </button>
                  {projectModalMode !== 'view' && (
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={saveProject}
                      disabled={isSavingProject}
                      style={{
                        borderRadius: '8px',
                        padding: '8px 24px',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}
                    >
                      {isSavingProject ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                          {projectModalMode === 'edit' ? 'Updating...' : 'Saving...'}
                        </>
                      ) : (
                        <>
                          <i className="feather-save me-1"></i>{projectModalMode === 'edit' ? 'Update' : 'Submit'}
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Animation Styles */}
            <style>{`
              @keyframes fadeIn {
                from { opacity: 0; }
                to { opacity: 1; }
              }
              @keyframes slideInRight {
                from { 
                  transform: translateX(100%);
                  opacity: 0;
                }
                to { 
                  transform: translateX(0);
                  opacity: 1;
                }
              }
              .ck-content-view h1,
              .ck-content-view h2,
              .ck-content-view h3,
              .ck-content-view h4,
              .ck-content-view h5,
              .ck-content-view h6 {
                margin-top: 0.75rem;
                margin-bottom: 0.5rem;
                font-weight: 600;
                line-height: 1.2;
              }
              .ck-content-view h1 { font-size: 1.5rem; }
              .ck-content-view h2 { font-size: 1.3rem; }
              .ck-content-view h3 { font-size: 1.1rem; }
              .ck-content-view p {
                margin-bottom: 0.75rem;
                line-height: 1.6;
              }
              .ck-content-view ul,
              .ck-content-view ol {
                margin-bottom: 0.75rem;
                padding-left: 1.5rem;
              }
              .ck-content-view li {
                margin-bottom: 0.25rem;
              }
              .ck-content-view table {
                width: 100%;
                border-collapse: collapse;
                margin: 0.75rem 0;
              }
              .ck-content-view table td,
              .ck-content-view table th {
                border: 1px solid #dee2e6;
                padding: 0.5rem;
                text-align: left;
              }
              .ck-content-view table th {
                background-color: #e9ecef;
                font-weight: 600;
              }
              .ck-content-view blockquote {
                border-left: 4px solid #0d6efd;
                padding-left: 1rem;
                margin: 0.75rem 0;
                font-style: italic;
                color: #6c757d;
              }
              .ck-content-view a {
                color: #0d6efd;
                text-decoration: none;
                cursor: pointer;
                pointer-events: auto;
              }
              .ck-content-view a:hover {
                text-decoration: underline;
                color: #0a58ca;
              }
              .ck-content-view a:visited {
                color: #6f42c1;
              }
              .ck-content-view strong {
                font-weight: 600;
              }
              .ck-content-view em {
                font-style: italic;
              }
            `}</style>
          </>
        )}
    </div>
  );
};
  
export default Upcoming;

